import React, { useState, useEffect, useMemo, useCallback } from "react";
import { History, ArrowLeft, Eye, IndianRupee } from "lucide-react";
import { Modal } from "react-bootstrap";
import { PageHeader, StatsCard, AppButton, SearchInput, AppBadge, EmptyState, Paginator } from "../../ui";
import apiClient from "../../../utils/apiClient";
import { useNavigate } from "react-router-dom";
import "./payouts.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const PAGE_SIZE = 20;

const SettlementHistoryPage = () => {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [totals, setTotals] = useState({ totalSettled: 0, totalCommission: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [viewPayout, setViewPayout] = useState(null);
  const [viewTxns, setViewTxns] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get("/sa/payouts/history", { params: { page, limit: PAGE_SIZE } });
      setPayouts(data.payouts || []);
      setTotals(data.totals || { totalSettled: 0, totalCommission: 0, count: 0 });
      setPagination(data.pagination || { page: 1, totalPages: 1, total: (data.payouts || []).length });
    } catch {
      // keep empty
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const filtered = useMemo(() => {
    if (!search) return payouts;
    const q = search.toLowerCase();
    return payouts.filter(
      (p) =>
        p.instituteId?.name?.toLowerCase().includes(q) ||
        p.settlementReference?.toLowerCase().includes(q)
    );
  }, [payouts, search]);

  const stats = useMemo(() => ({
    totalSettled: totals.totalSettled || 0,
    totalCommission: totals.totalCommission || 0,
    count: totals.count || 0,
  }), [totals]);

  const openView = async (payout) => {
    try {
      setViewLoading(true);
      setViewPayout(payout);
      const { data } = await apiClient.get(`/sa/payouts/${payout._id}`);
      setViewPayout(data.payout);
      setViewTxns(data.transactions || []);
    } catch {
      // keep payout
    } finally {
      setViewLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border" /></div>;
  }

  return (
    <div className="settlement-history">
      <PageHeader
        title="Settlement History"
        description="Past payouts settled to institutes"
        icon={History}
        actions={
          <AppButton variant="outline" size="sm" onClick={() => navigate("/sa/payouts")}>
            <ArrowLeft size={14} className="me-1" /> Pending Payouts
          </AppButton>
        }
      />

      <div className="sa-payouts__stats">
        <StatsCard title="Total Settled" value={formatCurrency(stats.totalSettled)} icon={IndianRupee} iconColor="emerald" />
        <StatsCard title="Commission Collected" value={formatCurrency(stats.totalCommission)} icon={IndianRupee} iconColor="blue" />
        <StatsCard title="Settlements" value={stats.count} icon={History} iconColor="violet" />
      </div>

      <div className="sa-payouts__toolbar">
        <div className="sa-payouts__search">
          <SearchInput value={search} onChange={setSearch} placeholder="Search institute or reference (current page)…" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={<History size={28} />} title="No Settlements Yet" description="No payouts have been settled yet." />
      ) : (
        <div className="sa-card">
          <div className="sa-card-head">Settlements ({filtered.length})</div>
          <div className="sa-card-body" style={{ overflowX: "auto", padding: 0 }}>
            <table className="payout-table">
              <thead>
                <tr>
                  <th>Institute</th>
                  <th>Net Paid</th>
                  <th>Commission</th>
                  <th>Txns</th>
                  <th>Period</th>
                  <th>Method</th>
                  <th>Settled On</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <div className="payout-table__name">{p.instituteId?.name || "Unknown"}</div>
                      <div className="payout-table__email">{p.instituteId?.email}</div>
                    </td>
                    <td className="payout-table__amount payout-table__amount--green">{formatCurrency(p.netPayable)}</td>
                    <td className="payout-table__amount">{formatCurrency(p.totalCommission)}</td>
                    <td>{p.transactionCount}</td>
                    <td className="payout-table__period">
                      {formatDate(p.periodStart)} – {formatDate(p.periodEnd)}
                    </td>
                    <td>
                      <AppBadge variant="secondary">{p.settlementMethod?.replace(/_/g, " ")}</AppBadge>
                    </td>
                    <td className="payout-table__period">{formatDate(p.settledAt)}</td>
                    <td>
                      <AppButton variant="outline" size="sm" onClick={() => openView(p)}>
                        <Eye size={14} />
                      </AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Paginator
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        pageSize={PAGE_SIZE}
        onChange={setPage}
        infoLabel="settlements"
      />

      {/* View Payout Detail Modal */}
      {viewPayout && (
        <Modal show onHide={() => { setViewPayout(null); setViewTxns([]); }} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1rem" }}>
              Settlement Detail — {viewPayout.instituteId?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="settle-form__summary mb-3">
              <div className="settle-form__row">
                <span className="settle-form__label">Total Collected</span>
                <span className="settle-form__value">{formatCurrency(viewPayout.totalCollected)}</span>
              </div>
              <div className="settle-form__row">
                <span className="settle-form__label">Commission</span>
                <span className="settle-form__value">{formatCurrency(viewPayout.totalCommission)}</span>
              </div>
              <div className="settle-form__row">
                <span className="settle-form__label">Net Paid</span>
                <span className="settle-form__value settle-form__value--green">{formatCurrency(viewPayout.netPayable)}</span>
              </div>
              <div className="settle-form__row">
                <span className="settle-form__label">Method</span>
                <span className="settle-form__value">{viewPayout.settlementMethod?.replace(/_/g, " ")}</span>
              </div>
              {viewPayout.settlementReference && (
                <div className="settle-form__row">
                  <span className="settle-form__label">Reference</span>
                  <span className="settle-form__value">{viewPayout.settlementReference}</span>
                </div>
              )}
              {viewPayout.settlementNotes && (
                <div className="settle-form__row">
                  <span className="settle-form__label">Notes</span>
                  <span className="settle-form__value">{viewPayout.settlementNotes}</span>
                </div>
              )}
              <div className="settle-form__row">
                <span className="settle-form__label">Settled By</span>
                <span className="settle-form__value">{viewPayout.settledBy?.name || "—"}</span>
              </div>
            </div>

            {viewLoading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
            ) : viewTxns.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table className="payout-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Amount</th>
                      <th>Commission</th>
                      <th>Net</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewTxns.map((t) => (
                      <tr key={t._id}>
                        <td>
                          <div className="payout-table__name">{t.studentId?.name || "—"}</div>
                          <div className="payout-table__email">{t.studentId?.email}</div>
                        </td>
                        <td className="payout-table__amount">{formatCurrency(t.amount)}</td>
                        <td className="payout-table__amount payout-table__amount--red">{formatCurrency(t.commissionAmount)}</td>
                        <td className="payout-table__amount payout-table__amount--green">{formatCurrency(t.netPayable)}</td>
                        <td className="payout-table__period">{formatDate(t.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default SettlementHistoryPage;
